import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Upload, CheckCircle, Users, Briefcase, TrendingUp, FileText, Ship, MapPin, DollarSign, Calendar, Filter } from 'lucide-react';
const benefits = [{
  icon: Briefcase,
  title: 'Verified Companies',
  description: 'Work with reliable shipowners and operators who value professionalism'
}, {
  icon: Users,
  title: 'Professional Matching',
  description: 'Your profile is reviewed by experienced maritime professionals'
}, {
  icon: TrendingUp,
  title: 'Career Growth',
  description: 'Access to diverse vessels and projects for professional development'
}, {
  icon: FileText,
  title: 'Document Support',
  description: 'Guidance with certificates, compliance, and requirements'
}];
const steps = [{
  number: '01',
  title: 'Submit Your CV',
  description: 'Upload your CV, certificates, and seafaring documents'
}, {
  number: '02',
  title: 'Profile Review',
  description: 'Our team reviews your qualifications and experience'
}, {
  number: '03',
  title: 'Matching',
  description: 'We match your profile with suitable vessels and positions'
}, {
  number: '04',
  title: 'Interview',
  description: 'Connect with companies for interviews and discussions'
}, {
  number: '05',
  title: 'Contract',
  description: 'Sign contract and prepare for embarkation'
}];
const requirements = ['Valid passport (minimum 6 months validity)', 'STCW certificates (Basic Safety Training)', 'Medical fitness certificate', "Seafarer's book / Discharge book", 'Certificate of Competency (COC) for officers', 'Endorsements and additional certificates'];
const vacancies = [{
  rank: 'Chief Engineer',
  vessel: 'Chemical Tanker',
  region: 'Europe',
  salary: '$7,500',
  contract: '4+2 months'
}, {
  rank: 'Second Officer',
  vessel: 'Container Vessel',
  region: 'Asia Pacific',
  salary: '$4,500',
  contract: '6+1 months'
}, {
  rank: 'AB Seaman',
  vessel: 'Bulk Carrier',
  region: 'Global',
  salary: '$2,500',
  contract: '8+2 months'
}];
const faqs = [{
  question: 'What documents do I need to apply?',
  answer: "You need a valid passport, STCW certificates, medical certificate, seafarer's book, and relevant certificates of competency for your rank."
}, {
  question: 'How long does the recruitment process take?',
  answer: 'Typically 2-4 weeks from CV submission to contract offer, depending on position availability and document verification.'
}, {
  question: 'Do you charge seafarers any fees?',
  answer: 'No, we do not charge seafarers any fees. Our services are free for maritime professionals.'
}, {
  question: 'What types of vessels do you recruit for?',
  answer: 'We recruit for tankers, bulk carriers, container vessels, offshore vessels, cruise ships, and specialized vessels.'
}];
export function SpecialistsPage() {
  const [dragActive, setDragActive] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  return <div className="min-h-screen bg-white" data-id="element-429">
      <Header data-id="element-430" />

      {/* Hero Section */}
      <section className="w-full px-6 md:px-12 py-16 bg-gradient-to-br from-[var(--color-dark)] to-[var(--color-dark-lighter)]" data-id="element-431">
        <div className="max-w-4xl mx-auto text-center" data-id="element-432">
          <div className="inline-block px-4 py-1.5 border border-gray-600 rounded-full mb-6" data-id="element-433">
            <span className="text-sm font-medium text-white" data-id="element-434">
              For Seafarers
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-435">
            Find Your Next Contract with Verified Companies
          </h1>
          <p className="text-xl text-gray-300 mb-8" data-id="element-436">
            Connect with reliable shipowners and operators. Professional
            matching, document support, and career growth opportunities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-437">
            <a href="#cv-form" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[var(--color-tan)] text-white text-base font-medium rounded-lg hover:opacity-90 transition-opacity" data-id="element-438">
              <Upload className="w-5 h-5" data-id="element-439" />
              Send CV
            </a>
            <a href="#vacancies" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[var(--color-dark)] text-base font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-440">
              View Vacancies
            </a>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-441">
        <div className="max-w-7xl mx-auto" data-id="element-442">
          <div className="text-center mb-12" data-id="element-443">
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-444">
              Why Work With ELVORA?
            </h2>
            <p className="text-lg text-[var(--color-text-light)] max-w-2xl mx-auto" data-id="element-445">
              Professional recruitment service for maritime professionals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-446">
            {benefits.map((benefit, index) => <div key={index} className="p-6 bg-white border border-gray-100 rounded-xl" data-id="element-447">
                <benefit.icon className="w-8 h-8 text-[var(--color-tan)] mb-4" strokeWidth={1.5} data-id="element-448" />
                <h3 className="text-lg font-semibold text-[var(--color-text-dark)] mb-2" data-id="element-449">
                  {benefit.title}
                </h3>
                <p className="text-sm text-[var(--color-text-light)]" data-id="element-450">
                  {benefit.description}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="w-full px-6 md:px-12 py-16 bg-[var(--color-bg)]" data-id="element-451">
        <div className="max-w-7xl mx-auto" data-id="element-452">
          <div className="text-center mb-12" data-id="element-453">
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-454">
              How It Works
            </h2>
            <p className="text-lg text-[var(--color-text-light)]" data-id="element-455">
              Simple and transparent process from CV submission to contract
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8" data-id="element-456">
            {steps.map((step, index) => <div key={index} className="relative" data-id="element-457">
                <div className="flex flex-col items-center text-center" data-id="element-458">
                  <div className="w-16 h-16 rounded-full bg-[var(--color-tan)] text-white flex items-center justify-center text-xl font-bold mb-4" data-id="element-459">
                    {step.number}
                  </div>
                  <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-2" data-id="element-460">
                    {step.title}
                  </h3>
                  <p className="text-sm text-[var(--color-text-light)]" data-id="element-461">
                    {step.description}
                  </p>
                </div>
                {index < steps.length - 1 && <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gray-200" data-id="element-462" />}
              </div>)}
          </div>
        </div>
      </section>

      {/* Vacancies */}
      <section id="vacancies" className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-463">
        <div className="max-w-7xl mx-auto" data-id="element-464">
          <div className="flex items-center justify-between mb-8" data-id="element-465">
            <div data-id="element-466">
              <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-2" data-id="element-467">
                Current Vacancies
              </h2>
              <p className="text-[var(--color-text-light)] text-sm" data-id="element-468">
                Browse available positions and apply directly
              </p>
            </div>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors" data-id="element-469">
              <Filter className="w-4 h-4" data-id="element-470" />
              <span className="text-sm" data-id="element-471">Filters</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-472">
            {vacancies.map((vacancy, index) => <div key={index} className="p-6 bg-white border border-gray-100 rounded-xl hover:border-gray-200 transition-colors" data-id="element-473">
                <h3 className="text-lg font-semibold text-[var(--color-text-dark)] mb-1" data-id="element-474">
                  {vacancy.rank}
                </h3>
                <p className="text-sm text-[var(--color-text-light)] mb-4 flex items-center gap-2" data-id="element-475">
                  <Ship className="w-4 h-4" data-id="element-476" />
                  {vacancy.vessel}
                </p>

                <div className="space-y-2 mb-6" data-id="element-477">
                  <div className="flex items-center gap-2 text-sm text-[var(--color-text-light)]" data-id="element-478">
                    <MapPin className="w-4 h-4" data-id="element-479" />
                    <span data-id="element-480">{vacancy.region}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[var(--color-text-light)]" data-id="element-481">
                    <DollarSign className="w-4 h-4" data-id="element-482" />
                    <span data-id="element-483">{vacancy.salary}/month</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[var(--color-text-light)]" data-id="element-484">
                    <Calendar className="w-4 h-4" data-id="element-485" />
                    <span data-id="element-486">{vacancy.contract}</span>
                  </div>
                </div>

                <button className="w-full px-4 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity" data-id="element-487">
                  Apply Now
                </button>
              </div>)}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="w-full px-6 md:px-12 py-16 bg-[var(--color-bg)]" data-id="element-488">
        <div className="max-w-4xl mx-auto" data-id="element-489">
          <div className="text-center mb-8" data-id="element-490">
            <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-491">
              Required Documents
            </h2>
            <p className="text-lg text-[var(--color-text-light)]" data-id="element-492">
              Standard documents needed for seafarer recruitment
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-8" data-id="element-493">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-494">
              {requirements.map((req, index) => <div key={index} className="flex items-start gap-3" data-id="element-495">
                  <CheckCircle className="w-5 h-5 text-[var(--color-tan)] flex-shrink-0 mt-0.5" data-id="element-496" />
                  <span className="text-sm text-[var(--color-text-dark)]" data-id="element-497">
                    {req}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* CV Submission Form */}
      <section id="cv-form" className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-498">
        <div className="max-w-4xl mx-auto" data-id="element-499">
          <div className="text-center mb-8" data-id="element-500">
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-501">
              Submit Your CV
            </h2>
            <p className="text-lg text-[var(--color-text-light)]" data-id="element-502">
              Fill in your details and upload your documents to start the
              process
            </p>
          </div>

          <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 md:p-8" data-id="element-503">
            <form className="space-y-5" data-id="element-504">
              {/* Personal Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-505">
                <div data-id="element-506">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-507">
                    Full Name <span className="text-red-500" data-id="element-508">*</span>
                  </label>
                  <input type="text" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-509" />
                </div>
                <div data-id="element-510">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-511">
                    Rank / Position <span className="text-red-500" data-id="element-512">*</span>
                  </label>
                  <input type="text" placeholder="e.g., Chief Engineer, AB Seaman" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-513" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-514">
                <div data-id="element-515">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-516">
                    Experience (years) <span className="text-red-500" data-id="element-517">*</span>
                  </label>
                  <input type="text" placeholder="e.g., 5 years" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-518" />
                </div>
                <div data-id="element-519">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-520">
                    Last Vessel Type <span className="text-red-500" data-id="element-521">*</span>
                  </label>
                  <input type="text" placeholder="e.g., Chemical Tanker" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-522" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-523">
                <div data-id="element-524">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-525">
                    Availability Date <span className="text-red-500" data-id="element-526">*</span>
                  </label>
                  <input type="date" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-527" />
                </div>
                <div data-id="element-528">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-529">
                    Preferred Vessel Type
                  </label>
                  <input type="text" placeholder="e.g., Tanker, Bulk Carrier" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-530" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-531">
                <div data-id="element-532">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-533">
                    Phone (WhatsApp/Telegram){' '}
                    <span className="text-red-500" data-id="element-534">*</span>
                  </label>
                  <input type="tel" placeholder="+38 (0XX) XXX XX XX" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-535" />
                </div>
                <div data-id="element-536">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-537">
                    Email <span className="text-red-500" data-id="element-538">*</span>
                  </label>
                  <input type="email" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-539" />
                </div>
              </div>

              {/* File Upload */}
              <div data-id="element-540">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-541">
                  Upload CV + Certificates{' '}
                  <span className="text-red-500" data-id="element-542">*</span>
                </label>
                <div onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${dragActive ? 'border-[var(--color-tan)] bg-[var(--color-tan)]/5' : 'border-gray-300 hover:border-gray-400'}`} data-id="element-543">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" data-id="element-544" />
                  <p className="text-sm text-[var(--color-text-dark)] mb-1" data-id="element-545">
                    Drag and drop files here, or click to browse
                  </p>
                  <p className="text-xs text-[var(--color-text-light)]" data-id="element-546">
                    CV, certificates, seafarer's book (PDF, images)
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-center pt-2" data-id="element-547">
                <button type="submit" className="px-8 py-4 bg-[var(--color-tan)] text-white text-base font-medium rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2" data-id="element-548">
                  <CheckCircle className="w-5 h-5" data-id="element-549" />
                  Submit Application
                </button>
              </div>

              <p className="text-xs text-[var(--color-text-light)] text-center" data-id="element-550">
                All information is treated confidentially. We review
                applications within 2-3 business days.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="w-full px-6 md:px-12 py-16 bg-[var(--color-bg)]" data-id="element-551">
        <div className="max-w-4xl mx-auto" data-id="element-552">
          <div className="text-center mb-12" data-id="element-553">
            <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-554">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-200" data-id="element-555">
            <div className="space-y-0" data-id="element-556">
              {faqs.map((faq, index) => <div key={index} className="border-b border-gray-100 last:border-0" data-id="element-557">
                  <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full py-5 flex items-start justify-between text-left gap-4" data-id="element-558">
                    <span className="text-base font-medium text-[var(--color-text-dark)]" data-id="element-559">
                      {faq.question}
                    </span>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${openFaq === index ? 'bg-[var(--color-tan)]' : 'bg-gray-100'}`} data-id="element-560">
                      <svg className={`w-4 h-4 transition-colors ${openFaq === index ? 'text-white' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-561">
                        {openFaq === index ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" data-id="element-562" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" data-id="element-563" />}
                      </svg>
                    </div>
                  </button>
                  {openFaq === index && <div className="pb-5" data-id="element-564">
                      <p className="text-sm text-[var(--color-text-light)]" data-id="element-565">
                        {faq.answer}
                      </p>
                    </div>}
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-566">
        <div className="max-w-4xl mx-auto text-center" data-id="element-567">
          <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-568">
            Need Help?
          </h2>
          <p className="text-lg text-[var(--color-text-light)] mb-8" data-id="element-569">
            Contact us via messenger or email for quick assistance
          </p>

          <div className="flex flex-wrap justify-center gap-4" data-id="element-570">
            <a href="https://t.me/elvora" className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors" data-id="element-571">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#0088cc" data-id="element-572">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" data-id="element-573" />
              </svg>
              <span className="text-sm font-medium" data-id="element-574">Telegram</span>
            </a>
            <a href="https://wa.me/380" className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors" data-id="element-575">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#25D366" data-id="element-576">
                <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0012.04 2z" data-id="element-577" />
              </svg>
              <span className="text-sm font-medium" data-id="element-578">WhatsApp</span>
            </a>
            <a href="mailto:info@elvora.com" className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors" data-id="element-579">
              <svg className="w-5 h-5 text-[var(--color-tan)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-580">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" data-id="element-581" />
              </svg>
              <span className="text-sm font-medium" data-id="element-582">Email</span>
            </a>
          </div>
        </div>
      </section>

      <Footer data-id="element-583" />
    </div>;
}